import { useSyncExternalStore } from 'react';

import { mermaidTheme } from './mermaid-theme.js';

/**
 * How a light drawing is shown in the dark app theme: inverted and turned back to its own hues, the
 * filter Excalidraw's dark mode applies to its canvas, so a Mermaid diagram and a whiteboard look
 * alike. It changes the screen only; the document, its thumbnails' data and every download stay as
 * drawn. It uses the `[data-theme='dark']` ancestor selector, because this app's `dark:` variant
 * only matches inside an element that is itself dark.
 */
export const DARK_THEME_DRAWING =
  "[[data-theme='dark']_&]:invert-93 [[data-theme='dark']_&]:hue-rotate-180";

export type AppTheme = 'light' | 'dark';

/**
 * Whether a Mermaid drawing should follow the app theme. A diagram whose own theme is already dark
 * is drawn for a dark page, and inverting it would make it light again.
 */
export function mermaidFollowsAppTheme(source: string): boolean {
  return mermaidTheme(source) !== 'dark';
}

function readAppTheme(): AppTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function watchAppTheme(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  return () => observer.disconnect();
}

/**
 * The app's theme as the page shows it right now, read from the root's `data-theme`. It works under
 * any provider tree, including the public share page.
 */
export function useAppTheme(): AppTheme {
  return useSyncExternalStore(watchAppTheme, readAppTheme, () => 'light');
}
